import classNames from '@sindresorhus/class-names';
import {safeSessionStorage} from '@utils/safeStorages';
import {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {YandexBannerAd} from '../../Banner/YaBanner';
import {SESSION_STORAGE_KEY} from '../../consts';
import {useAdAnalytics} from '../../hooks/useAdAnalytics';
import {AdFoxConfig, AdType, BannerPlacementType} from '../../types';
import styles from './YaMobileFloorBanner.module.scss';

type AppearMode = 'after_scroll' | 'on_mount';

type Props = {
  blockId: string;
  adFoxConfig: AdFoxConfig;
  appearMode: AppearMode;
};

const appearModeConfig: Record<
  AppearMode,
  {adType: AdType; placement: BannerPlacementType}
> = {
  after_scroll: {
    adType: 'floor_ad_banner_scroll',
    placement: 'floor_ad_banner_scroll',
  },
  on_mount: {
    adType: 'floor_ad_banner',
    placement: 'floor_ad_banner',
  },
};

/**
 * Этот компонент рендерится исключительно на мобилке, при включенном floor_ad_banner(_scroll),
 * выглядит как баннер снизу (если appearMode=after_scroll, то появляется после скролла),
 * но использует внутри себя обычный YaBanner
 */
export const YaMobileFloorBanner = ({
  blockId,
  appearMode,
  adFoxConfig,
}: Props) => {
  const rootRef = useRef(document.getElementById('root'));
  const [scrolled, setScrolled] = useState(false);
  const [failed, setFailed] = useState(false);
  const [isClosed, setClosed] = useState(false);
  const [isClosedInSession] = useState(
    safeSessionStorage.getItem(SESSION_STORAGE_KEY),
  );

  const {closed} = useAdAnalytics({
    adType: appearModeConfig[appearMode].adType,
    platform: adFoxConfig?.enabled ? 'adfox' : 'yandex',
  });

  useEffect(() => {
    const root = rootRef.current;
    if (root) {
      const scrollOffset = document.documentElement.scrollTop;
      root.classList.add(styles.rootContainer);
      root.scrollTop = scrollOffset;
    }
    // on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (scrolled || !root || appearMode === 'on_mount') {
      return;
    }

    const onScrolled = () => setScrolled(true);

    if (root.scrollTop > 0) {
      // уже поскроллились - можно и показать рекламу
      onScrolled();
      return;
    }

    // на мобильном сафари scrollend не поддерживается, поэтому scroll + таймауты
    let timeoutHandle: NodeJS.Timeout | null = null;

    const listener = () => {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
      timeoutHandle = setTimeout(onScrolled, 50);
    };
    root.addEventListener('scroll', listener);

    return () => {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
      root.removeEventListener('scroll', listener);
    };
  }, [blockId, scrolled, appearMode]);

  const shouldShowAd =
    (scrolled || appearMode === 'on_mount') &&
    !failed &&
    !isClosed &&
    !isClosedInSession;

  useEffect(() => {
    if (shouldShowAd) {
      rootRef.current?.classList.add(styles.adShown);
    } else {
      rootRef.current?.classList.remove(styles.adShown);
    }
  }, [shouldShowAd, blockId]);

  const onErrorOrNoAd = useCallback(() => setFailed(true), []);

  const onRender = useCallback(() => setFailed(false), []);

  const onClose = () => {
    closed(blockId);
    setClosed(true);
    safeSessionStorage.setItem(SESSION_STORAGE_KEY, '1');
  };

  if (!shouldShowAd) {
    return null;
  }

  return (
    <>
      {createPortal(
        <div
          className={classNames(
            styles.adContainer,
            shouldShowAd && styles.adShown,
          )}
        >
          <button className={styles.closeButton} onClick={onClose}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.71967 0.71967C1.01256 0.426777 1.48744 0.426777 1.78033 0.71967L6 4.93934L10.2197 0.71967C10.5126 0.426777 10.9874 0.426777 11.2803 0.71967C11.5732 1.01256 11.5732 1.48744 11.2803 1.78033L7.06066 6L11.2803 10.2197C11.5732 10.5126 11.5732 10.9874 11.2803 11.2803C10.9874 11.5732 10.5126 11.5732 10.2197 11.2803L6 7.06066L1.78033 11.2803C1.48744 11.5732 1.01256 11.5732 0.71967 11.2803C0.426777 10.9874 0.426777 10.5126 0.71967 10.2197L4.93934 6L0.71967 1.78033C0.426777 1.48744 0.426777 1.01256 0.71967 0.71967Z"
                fill="#6D7885"
              />
            </svg>
          </button>
          <YandexBannerAd
            bannerContainerClassName={styles.ad}
            blockId={blockId}
            adType={appearModeConfig[appearMode].adType}
            placement={appearModeConfig[appearMode].placement}
            onRender={onRender}
            onError={onErrorOrNoAd}
            onNoAds={onErrorOrNoAd}
            adFoxConfig={adFoxConfig}
          />
        </div>,
        document.body,
      )}
    </>
  );
};
